import { Injectable } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';

@Injectable({
  providedIn: 'root'
})
export class StatusBarService {
  constructor() {
    this.initializeStatusBar();
  }

  private async initializeStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return; // Skip if running in browser
    }

    try {
      // Wait for the device to be ready
      await this.waitForDeviceReady();
      
      // Status bar should not overlay content to avoid white bar at top
      await StatusBar.setOverlaysWebView({ overlay: false });
      
      // Native status bar (literal required, no CSS vars): brand page surface #FAF6F0 (--p-surfacepage)
      await StatusBar.setBackgroundColor({ color: '#FFFAF6F0' });
      
      // Detect and apply the correct style based on device color scheme
      await this.applyThemeBasedStyle();
      
      // Initialize safe area handling
      this.initializeSafeArea();
      
      // Setup color scheme change handling
      this.setupColorSchemeHandling();
      
    } catch (error) {
      console.warn('StatusBar initialization failed:', error);
    }
  }

  /**
   * Wait for Capacitor platform to be ready
   */
  private waitForDeviceReady(): Promise<void> {
    return new Promise((resolve) => {
      // If document is already ready, resolve immediately
      if (document.readyState === 'complete') {
        resolve();
      } else {
        // Otherwise wait for window load event
        window.addEventListener('load', () => resolve(), { once: true });
      }
    });
  }

  /**
   * Update the status bar background color based on theme
   */
  private async updateStatusBarBackground(): Promise<void> {
    // Always use the brand page surface (matches --p-surfacepage)
    // Format: #AARRGGBB where AA is alpha (FF = fully opaque)
    await StatusBar.setBackgroundColor({ 
      color: '#FFFAF6F0' // brand page surface (--p-surfacepage), fully opaque
    });
  }

  /**
   * Detect device color scheme and apply appropriate status bar style
   */
  private async applyThemeBasedStyle(): Promise<void> {
    // Always use light style (dark text) since we have a semi-transparent background
    // This ensures the text is always readable regardless of system theme
    await this.setLightStyle();
    
    // Update the background color for semi-transparency
    await this.updateStatusBarBackground();
    
    console.log('Status bar style applied: light mode (dark text for readability)');
  }

  /**
   * Check if device is in dark mode
   */
  private isDarkMode(): boolean {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  }

  /**
   * Setup listener for color scheme changes
   */
  private setupColorSchemeHandling(): void {
    if (window.matchMedia) {
      const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      // Modern approach with addEventListener
      darkModeQuery.addEventListener('change', (e) => {
        console.log(`Color scheme changed to: ${e.matches ? 'dark' : 'light'} mode`);
        this.applyThemeBasedStyle();
      });
    }
  }

  /**
   * Tag <body> with the platform so styles can branch per platform.
   *
   * Insets are NOT measured here. On Android, Capacitor's built-in SystemBars
   * plugin (insetsHandling: 'css') writes the real system bar insets to
   * --safe-area-inset-* on <html>; on iOS safe-area.scss reads env().
   */
  private initializeSafeArea(): void {
    const platform = Capacitor.getPlatform();
    if (platform === 'android' || platform === 'ios') {
      document.body.classList.add(`capacitor-${platform}`);
    }
  }

  async setLightStyle(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
      await StatusBar.setStyle({ style: Style.Light });
    } catch (error) {
      console.warn('Failed to set light status bar style:', error);
    }
  }

  async setDarkStyle(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
      await StatusBar.setStyle({ style: Style.Dark });
    } catch (error) {
      console.warn('Failed to set dark status bar style:', error);
    }
  }

  async hideStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
      await StatusBar.hide();
    } catch (error) {
      console.warn('Failed to hide status bar:', error);
    }
  }

  async showStatusBar(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;
    
    try {
      await StatusBar.show();
    } catch (error) {
      console.warn('Failed to show status bar:', error);
    }
  }
}
